<template>
  <Spinner v-if="loading"/>
  <div v-else class="salary-overview-app">
    <h2 v-if="statisticsHeading">{{ statisticsHeading }}</h2>
    <p v-if="statisticsDescription"><strong>{{ statisticsDescription }}</strong></p>
    <div class="salary-overview-app__column-wrapper">
      <div class="salary-overview-app__column">
        <Accordion
          v-for="(region, index) in firstRegions"
          :key="region.region"
          :title="formatText(region.region)"
          :defaultExpanded="index === 0"
          :toggleRight="true"
          :white="true"
          class="accordion-block--statistics">
          <SalaryResultsStatistics
            :statistics="toStatistics(region)"
            :values="null"
            :valuesLabel="null"
            :p10Label="translations?.p10Label"
            :medianLabel="translations?.medianSalaryLabel"
            :averageLabel="translations?.averageSalaryLabel"
            :p90Label="translations?.p90Label"
            :insufficientDataTitle="translations?.insufficientDataOne"
            :insufficientDataText="translations?.insufficientDataTwo"/>
          <div class="salary-overview-app__statistics-info">
            <p v-if="translations?.overviewStatisticsDescription" class="salary-overview-app__statistics-description">
              {{ translations.overviewStatisticsDescription }}
            </p>
            <SalaryExplanationLink :label="explanationLinkLabel" :url="explanationLinkUrl" />
          </div>
        </Accordion>
      </div>
      <div class="salary-overview-app__column">
        <Accordion
          v-for="region in secondRegions"
          :key="region.region"
          :title="formatText(region.region)"
          :toggleRight="true"
          :white="true"
          class="accordion-block--statistics">
          <SalaryResultsStatistics
            :statistics="toStatistics(region)"
            :values="null"
            :valuesLabel="null"
            :p10Label="translations?.p10Label"
            :medianLabel="translations?.medianSalaryLabel"
            :averageLabel="translations?.averageSalaryLabel"
            :p90Label="translations?.p90Label"
            :insufficientDataTitle="translations?.insufficientDataOne"
            :insufficientDataText="translations?.insufficientDataTwo"/>
          <div class="salary-overview-app__statistics-info">
            <p v-if="translations?.overviewStatisticsDescription" class="salary-overview-app__statistics-description">
              {{ translations.overviewStatisticsDescription }}
            </p>
            <SalaryExplanationLink :label="explanationLinkLabel" :url="explanationLinkUrl" />
          </div>
        </Accordion>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, onBeforeMount, ref} from 'vue';
import {useApi} from '@shared/services/api-service.ts';
import Spinner from '@shared/components/spinner/Spinner.vue';
import Accordion from "@shared/components/Accordion/Accordion.vue";
import SalaryExplanationLink from "@shared/components/salary-explanation-link/SalaryExplanationLink.vue";
import SalaryResultsStatistics from "@shared/components/salary-results-statistics/SalaryResultsStatistics.vue";
import {formatText} from '@shared/utils/format-text';

defineProps<{
  statisticsHeading: string | null;
  statisticsDescription: string | null;
  explanationLinkLabel: string | null;
  explanationLinkUrl: string | null;
}>();

const translationApi = useApi('salary/gettranslations');
const statisticsApi = useApi('salary/overview');

const translations = ref<SalaryAppTranslations | null>(null);
const regionStatistics = ref<RegionStatisticsResponse[]>([]);

const loading = computed(() => translationApi.loading.value || statisticsApi.loading.value);

const sortedRegions = computed(() => {
    return [...regionStatistics.value];
});

const splitIndex = computed(() => {
  return Math.ceil(sortedRegions.value.length / 2);
});

const firstRegions = computed(() => {
  return sortedRegions.value.slice(0, splitIndex.value);
});

const secondRegions = computed(() => {
  return sortedRegions.value.slice(splitIndex.value);
});

const toStatistics = (region: RegionStatisticsResponse): SalaryStatisticsValues => ({
  count: region.count,
  average: region.average,
  p10: region.p10,
  median: region.median,
  p90: region.p90,
  hasEnoughData: region.count > 10,
});

onBeforeMount(() => {
  Promise.all([
    translationApi.get<SalaryAppTranslations>(),
    statisticsApi.get<RegionStatisticsResponse[]>(),
  ])
    .then(([salaryTranslations, overview]) => {
      translations.value = salaryTranslations;
      regionStatistics.value = overview;
    })
    .catch((error: unknown) => {
      console.error('Error fetching salary overview data:', error);
    });
});
</script>
